import { type ReactNode, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";

import { useAppSelector } from "app/hooks";
import {
  type OptInState,
  readOptInState
} from "features/scheduling-v3/domain/settings-document";
import {
  selectPatientCreateAppointmentModeValues,
  selectReschedulingValues
} from "features/scheduling/scheduler-slice";

import { trackSchedulerGuideOpened } from "./scheduler-choice-telemetry";
import { GUIDE_LINK_TARGET, SCHEDULER_GUIDE_URL } from "./scheduler-guide";
import { useSchedulerChoice } from "./use-scheduler-choice.hook";
import { useSwitchScheduler } from "./use-switch-scheduler.hook";

const OFFER_PAUSE_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * "Not now" hides the offer for 14 days, then it comes back once; a second "Not now" is
 * final (ENG-1981, Chen 2026-09-27). An opt-out rests it for 14 days too (D13, 2026-09-28):
 * someone who just said why they are leaving is not offered it again at once. Stored in the
 * person's document, not the browser, so it follows them to every computer.
 */
export function isOfferDue(
  { notNow, optOuts }: OptInState,
  now: number
): boolean {
  const lastOptOut = optOuts.at(-1)?.at;
  if (lastOptOut && now - new Date(lastOptOut).getTime() < OFFER_PAUSE_MS) {
    return false;
  }
  if (notNow.count === 0) return true;
  if (notNow.count >= 2 || !notNow.lastAt) return false;
  return now - new Date(notNow.lastAt).getTime() >= OFFER_PAUSE_MS;
}

const GRID_PATHS = new Set(["/scheduling", "/scheduling/"]);

/**
 * ENG-2384: the guide, for someone deciding whether to switch. A quiet text link, never a
 * button, so "Try the new scheduler" stays the one obvious action.
 */
function WhatsNewLink({ source }: { source: "banner" | "classic_strip" }) {
  return (
    <Link
      href={SCHEDULER_GUIDE_URL}
      {...GUIDE_LINK_TARGET}
      variant="body2"
      underline="always"
      aria-label="What's new? (opens in a new tab)"
      onClick={() => trackSchedulerGuideOpened(source)}
      sx={{ fontWeight: 700, textUnderlineOffset: 3 }}
    >
      What&apos;s new?
    </Link>
  );
}

/**
 * ENG-1981: the invitation to try the new scheduler, above the classic grid. Rendered by the
 * app shell because the classic scheduler is frozen. Styled like the QuickBooks reconnect
 * banner (`qbo-reconnect-banner.component.tsx`) so two stacked banners read as one family.
 *
 * It WRAPS the classic scheduler rather than sitting beside it: the route's container lays
 * its children out in a row, so a sibling banner became a column that squeezed the grid
 * (caught in the browser, 2026-09-27). For anyone not offered the switch — everyone the
 * flag does not name — the classic scheduler renders exactly as it did before.
 */
export function SchedulerOfferBanner({ children }: { children: ReactNode }) {
  const { pathname, state: routeState, key: visitKey } = useLocation();
  const { offered, status, useV3, document } = useSchedulerChoice();
  const { optIn, notNow } = useSwitchScheduler();
  const [hidden, setHidden] = useState(false);
  // Classic's patient-booking and rescheduling modes ("Schedule on Calendar" from a chart, or
  // a reschedule) pin their own panel over the top of the page, where it covered the banner.
  // That moment is for finishing a booking, so the offer waits until it is over. Classic only
  // starts a mode in an effect after its first render, so the arrival itself (a hand-off
  // carrying a `patient`) counts too, until the mode has started and ended on this visit.
  const patientMode = Boolean(
    useAppSelector(selectPatientCreateAppointmentModeValues).patient
  );
  const rescheduling = Boolean(
    useAppSelector(selectReschedulingValues).originalAppointmentData
  );
  const inBookingMode = patientMode || rescheduling;
  const booking = useRef({ visitKey, started: false });
  if (booking.current.visitKey !== visitKey) {
    booking.current = { visitKey, started: false };
  }
  if (inBookingMode) booking.current.started = true;
  const arrivingToBook =
    !booking.current.started &&
    Boolean((routeState as { patient?: unknown } | null)?.patient);
  const bookingInProgress = inBookingMode || arrivingToBook;

  // The wrapper is decided by what cannot change during a classic visit — offered, and not
  // opted in — never by whether the banner is due. "Not now", its save, and moving between
  // classic's own pages all change "due"; if the wrapper followed it, the tree would change
  // shape and remount the whole classic scheduler (seen in the browser). Anyone not offered
  // the switch gets the classic scheduler exactly as before.
  if (!offered || useV3 || status !== "settled") return <>{children}</>;
  const onGrid = !bookingInProgress && GRID_PATHS.has(pathname);
  const due =
    onGrid && !hidden && isOfferDue(readOptInState(document), Date.now());
  // While the offer rests (after "Not now" or an opt-out), there is still always a way
  // across: a one-line strip in the same place, the mirror of V3's "You're on the new
  // scheduler" band (Silver, Chen 2026-09-29). It has no "Not now" of its own.
  const showStrip = onGrid && !due;

  const onNotNow = () => {
    // Hidden for this visit whatever happens to the save.
    setHidden(true);
    void notNow();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minWidth: 0,
        minHeight: 0
      }}
    >
      {due && (
        <Box
          role="region"
          aria-label="Try the new scheduler"
          data-testid="scheduler-offer-banner"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 0.75,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            borderBottom: (theme) =>
              `1px solid ${alpha(theme.palette.primary.main, 0.25)}`
          }}
        >
          <AutoAwesomeOutlinedIcon
            aria-hidden="true"
            fontSize="small"
            sx={{ color: "primary.main", flexShrink: 0 }}
          />
          <Typography variant="body2" sx={{ flex: 1 }}>
            <strong>A new scheduler is ready to try.</strong> Same appointments
            and patients, larger text, fewer clicks. You can switch back at any
            time. <WhatsNewLink source="banner" />
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={() => void optIn("banner")}
            sx={{ flexShrink: 0 }}
          >
            Try the new scheduler
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={onNotNow}
            sx={{ flexShrink: 0, color: "text.primary" }}
          >
            Not now
          </Button>
        </Box>
      )}
      {showStrip && (
        <Box
          role="region"
          aria-label="Which scheduler"
          data-testid="scheduler-switch-strip"
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            columnGap: 1,
            px: 2,
            py: 0.5,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            borderBottom: (theme) =>
              `1px solid ${alpha(theme.palette.primary.main, 0.25)}`
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: "primary.main" }}
          >
            You&apos;re on the classic scheduler
          </Typography>
          <Typography variant="body2" aria-hidden="true" color="text.secondary">
            ·
          </Typography>
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="always"
            onClick={() => void optIn("classic_strip")}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              fontWeight: 700,
              textUnderlineOffset: 3,
              "&:hover": { textDecorationThickness: 2 }
            }}
          >
            <SwapHorizIcon aria-hidden="true" fontSize="small" />
            Switch to the new scheduler
          </Link>
          <Typography variant="body2" aria-hidden="true" color="text.secondary">
            ·
          </Typography>
          <WhatsNewLink source="classic_strip" />
        </Box>
      )}
      {children}
    </Box>
  );
}
